import * as React from 'react';

import cn from 'classnames';

import {
  IBasicStyledProps,
  IconRegularChevronLeft,
  IconRegularChevronRight,
} from '@notino/react-styleguide';

import { useArrows } from '@hooks/useArrows';
import { assignRefs } from '@utils/assignRefs';

import {
  Arrow,
  circleArrowClass,
  OverflowFadeWrapper,
  ScrollableWrapper,
} from './styled';

type Props = {
  children: React.PropsWithChildren['children'];
  scrollableElRef?: React.RefObject<HTMLDivElement>;
  config?: Parameters<typeof useArrows>[1];
  deps?: Parameters<typeof useArrows>[0];
  arrowType?: 'chevron' | 'chevron-in-circle';
  arrowOffset?: string;
  showFadeOnlyOn?: 'desktop';
} & IBasicStyledProps;

export const OverflowFade = ({
  children,
  config,
  deps,
  scrollableElRef,
  arrowType = 'chevron',
  arrowOffset = '0.25rem',
  showFadeOnlyOn,
  ...rest
}: Props) => {
  const { containerRef, showLeftArrow, showRightArrow, scrollTo } = useArrows(
    deps,
    config
  );

  const arrowClass = cn({
    [circleArrowClass]: arrowType === 'chevron-in-circle',
  });

  return (
    <OverflowFadeWrapper
      {...rest}
      leftOverflow={showLeftArrow}
      rightOverflow={showRightArrow}
      showOnlyOnDesktop={showFadeOnlyOn === 'desktop'}
    >
      {showLeftArrow && (
        <Arrow
          position="left"
          onClick={() => scrollTo('left')}
          role="button"
          className={arrowClass}
          offset={arrowOffset}
        >
          <IconRegularChevronLeft
            color="icon.primary"
            height="1.25rem"
            width="1.25rem"
          />
        </Arrow>
      )}

      <ScrollableWrapper
        ref={
          scrollableElRef
            ? assignRefs(containerRef, scrollableElRef)
            : containerRef
        }
      >
        {children}
      </ScrollableWrapper>

      {showRightArrow && (
        <Arrow
          position="right"
          onClick={() => scrollTo('right')}
          role="button"
          className={arrowClass}
          offset={arrowOffset}
        >
          <IconRegularChevronRight
            color="icon.primary"
            height="1.25rem"
            width="1.25rem"
          />
        </Arrow>
      )}
    </OverflowFadeWrapper>
  );
};
